// src/components/validationNotes/presentationNotes.jsx
// ============================================================================
// L'écriture d'une feuille de notes d'enseignant, partagée par la file du
// carnet (« Saisies des enseignants »), la page d'une feuille, la page du mois
// d'une classe du primaire et l'onglet « Notes » du suivi des enseignants
// ============================================================================
//
// Une seule façon de dire un statut, une épreuve, un mois, une note : sinon la
// file dirait une chose là où le suivi en dirait une autre pour la même
// feuille. Rien ici ne décide : le statut, les valeurs et ce que fera le
// report (`prevision`) viennent du serveur, qui applique la MÊME règle pour
// annoncer et pour écrire (NotesEnseignantService.decisionDuReport).
//
// Consignes de Daniel à la validation de la maquette N3 (7 octobre 2026),
// tenues ici pour toute l'administration des notes :
//   - un badge, un filtre, une pastille ont l'arrondi d'un bouton (8 px,
//     `rounded-lg`), jamais une pilule ;
//   - le tiret court « - » dans tout texte affiché, jamais le tiret long ;
//   - un retour vers une autre page est un bouton bordé (`BoutonRetour`),
//     jamais un lien nu ;
//   - le titre d'une page est en gras prononcé (`font-bold`).
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { estVrai, libelleDe, nombre, nomPersonne, pluriel } from '../suiviEnseignants/formatSuivi';

// Les positions du JSON `notes.notes` du carnet (TableauNotes, bulletin du
// collège, espace famille) : l'épreuve d'une feuille EST sa position. Le
// serveur envoie aussi ses libellés (`epreuve_libelle`) : ils l'emportent.
export const EPREUVES = [
  { position: 0, libelle: 'Évaluation de classe' },
  { position: 1, libelle: 'Évaluation départementale' },
  { position: 2, libelle: 'Composition' }
];

export const libelleEpreuve = (epreuve) => EPREUVES[Number(epreuve)]?.libelle || `Épreuve ${epreuve ?? '-'}`;

const TRIMESTRES = { 1: '1er trimestre', 2: '2e trimestre', 3: '3e trimestre' };
export const libelleTrimestre = (trimestre) => TRIMESTRES[String(trimestre)] || `Trimestre ${trimestre ?? '-'}`;

// ----------------------------------------------------------------------------
// Niveau, mois et barème (N3 : le primaire, noté par mois, sur 10)
// ----------------------------------------------------------------------------

/** L'identifiant du mois d'une feuille du primaire, ou null (collège et lycée : mois 0). */
export function moisIdDe(feuille) {
  const id = Number(feuille?.mois?.id ?? feuille?.mois_id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Une feuille du primaire ? Le serveur le dit (`niveau`) ; à défaut, son mois
 * (`mois_id > 0`) : le collège et le lycée gardent le mois 0.
 */
export const estPrimaire = (feuille) => feuille?.niveau === 'primaire'
  || (feuille?.niveau !== 'secondaire' && moisIdDe(feuille) !== null);

/** « Octobre 2026 » : le nom du mois d'une feuille (objet `{ id, nom }` ou chaîne), ou ''. */
export const libelleMois = (feuille) => feuille?.mois_libelle || libelleDe(feuille?.mois) || '';

// Le barème par niveau (décision D1) : le serveur l'envoie et l'emporte ;
// ceux-ci ne servent que si une réponse ne le porte pas.
export const BAREMES = {
  primaire: { max: 10, pas: 0.5 },
  secondaire: { max: 20, pas: 0.25 }
};

/** `{ max, pas }` : celui du serveur s'il est lisible, sinon celui du niveau. */
export function baremeDe(source, primaire = estPrimaire(source)) {
  const brut = source?.bareme;
  const max = Number(brut?.max);
  const pas = Number(brut?.pas);
  const defaut = primaire ? BAREMES.primaire : BAREMES.secondaire;
  return {
    max: Number.isFinite(max) && max > 0 ? max : defaut.max,
    pas: Number.isFinite(pas) && pas > 0 ? pas : defaut.pas
  };
}

/** « 6e A · Mathématiques » - le nom d'une feuille dans une liste ou un titre. */
export const intituleFeuille = (feuille) => [libelleDe(feuille?.classe), libelleDe(feuille?.matiere)]
  .filter(Boolean)
  .join(' · ') || 'Feuille de notes';

/** « 1er trimestre · Composition » ; au primaire « Octobre 2026 · 1er trimestre ». */
export const periodeFeuille = (feuille) => {
  const trimestre = feuille?.trimestre_libelle || (feuille?.trimestre ? libelleTrimestre(feuille.trimestre) : '');
  if (estPrimaire(feuille)) return [libelleMois(feuille) || 'Mois', trimestre].filter(Boolean).join(' · ');
  return [trimestre || libelleTrimestre(feuille?.trimestre), feuille?.epreuve_libelle || libelleEpreuve(feuille?.epreuve)].join(' · ');
};

/** « CM2 A - Octobre 2026 » : le nom du mois d'une classe. */
export const intituleMois = (classe, mois) => [libelleDe(classe), libelleDe(mois)].filter(Boolean).join(' - ') || 'Mois de la classe';

/** L'adresse de la page du mois d'une classe du primaire. */
export const adresseMois = (classeId, moisId) => `/administration/notes/saisies/primaire/${encodeURIComponent(classeId)}/${encodeURIComponent(moisId)}`;

/** Le nom d'un employé tel que le serveur l'envoie (`{ id, nom }`, nom complet, ou prénom et nom), ou ''. */
export const nomEmploye = (personne) => libelleDe(personne) || (personne && typeof personne === 'object' ? nomPersonne(personne) : '');

// ----------------------------------------------------------------------------
// Statuts
// ----------------------------------------------------------------------------

// Les badges : même arrondi qu'un bouton (consigne de Daniel), tons de sens
// constants (DESIGN-SYSTEM.md).
export const TONS_BADGE = {
  info: 'border-[#246990]/25 bg-[#eef7fb] text-[#246990]',
  succes: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  attention: 'border-amber-200 bg-amber-50 text-amber-800',
  erreur: 'border-red-200 bg-red-50 text-red-700',
  teal: 'border-[#319190]/30 bg-[#319190]/10 text-[#25706f]',
  neutre: 'border-gray-200 bg-gray-100 text-gray-600'
};

const BADGE = 'inline-flex h-6 items-center justify-center whitespace-nowrap rounded-lg border text-xs font-medium';

/** Un badge d'état : `largeur` fixe dès qu'il se répète d'une ligne à l'autre. */
export const BadgeEtat = ({ ton = 'neutre', largeur = null, className = '', children }) => (
  <span className={`${BADGE} ${largeur ? '' : 'px-3'} ${TONS_BADGE[ton] || TONS_BADGE.neutre} ${className}`} style={largeur ? { width: largeur } : undefined}>
    {children}
  </span>
);

// Vus de la direction, avec les mots de la maquette validée : bleu de
// l'administration pour ce qui l'attend, ambre pour une rectification à
// décider, rouge pour un renvoi (un refus), vert pour ce qui est au carnet,
// teal pour une feuille rouverte chez l'enseignant, gris pour une saisie pas
// encore soumise — ou pas encore commencée (`a_saisir`, page du mois).
export const STATUTS_FEUILLE = {
  a_saisir: ['À saisir', 'neutre'],
  brouillon: ['Brouillon', 'neutre'],
  soumise: ['Soumise', 'info'],
  renvoyee: ['Renvoyée', 'erreur'],
  validee: ['Validée', 'succes'],
  rectification_demandee: ['Rectification demandée', 'attention'],
  rectification_autorisee: ['Rectification autorisée', 'teal']
};

/** Badge de largeur fixe : il se répète d'une ligne à l'autre. */
export const BadgeStatutFeuille = ({ statut, libre = false }) => {
  const [libelle, ton] = STATUTS_FEUILLE[statut] || [statut || '-', 'neutre'];
  return <BadgeEtat ton={ton} largeur={libre ? null : 176}>{libelle}</BadgeEtat>;
};

export const STATUTS_DEMANDE = {
  en_attente: ['En attente', 'attention'],
  autorisee: ['Autorisée', 'succes'],
  refusee: ['Refusée', 'erreur']
};

export const BadgeStatutDemande = ({ statut }) => {
  const [libelle, ton] = STATUTS_DEMANDE[statut] || [statut || '-', 'neutre'];
  return <BadgeEtat ton={ton}>{libelle}</BadgeEtat>;
};

/**
 * L'état du mois d'une classe du primaire, d'après les comptes du serveur
 * (`mois_primaire` de la file) : `{ libelle, ton }`.
 *
 * `partiel` : les comptes ne portent que sur les feuilles d'UN enseignant
 * (filtre `enseignant_id` : onglet « Notes » du suivi, filtre « Enseignant »
 * de la file), alors que `total_matieres` et `a_saisir` restent ceux de toute
 * la classe. Les comparer dirait « Validé en partie » d'un enseignant dont la
 * seule matière est validée : on ne juge alors que ses feuilles.
 */
export function etatDuMois(mois, { partiel = false } = {}) {
  const total = nombre(mois?.total_matieres);
  const validees = nombre(mois?.validees);
  const enCours = nombre(mois?.brouillons) + nombre(mois?.renvoyees);
  if (nombre(mois?.a_traiter) > 0 || nombre(mois?.soumises) > 0) return { libelle: 'À valider', ton: 'info' };
  if (partiel ? validees > 0 && enCours === 0 : total > 0 && validees >= total) return { libelle: 'Validé', ton: 'succes' };
  if (nombre(mois?.renvoyees) > 0) return { libelle: 'Renvoyé en partie', ton: 'erreur' };
  if (validees > 0) return { libelle: 'Validé en partie', ton: 'succes' };
  return { libelle: 'En saisie', ton: 'neutre' };
}

/**
 * « 7 soumises · 1 brouillon · 1 à saisir » : où en sont les matières du mois.
 * `partiel` (comptes d'un seul enseignant) : sans « à saisir », compté sur
 * toute la classe et donc faux pour lui.
 */
export function resumeDuMois(mois, { partiel = false } = {}) {
  return [
    nombre(mois?.soumises) > 0 ? pluriel(nombre(mois.soumises), 'soumise', 'soumises') : '',
    nombre(mois?.validees) > 0 ? pluriel(nombre(mois.validees), 'validée', 'validées') : '',
    nombre(mois?.renvoyees) > 0 ? pluriel(nombre(mois.renvoyees), 'renvoyée', 'renvoyées') : '',
    nombre(mois?.brouillons) > 0 ? pluriel(nombre(mois.brouillons), 'brouillon', 'brouillons') : '',
    !partiel && nombre(mois?.a_saisir) > 0 ? `${nombre(mois.a_saisir)} à saisir` : ''
  ].filter(Boolean).join(' · ');
}

// ----------------------------------------------------------------------------
// Notes
// ----------------------------------------------------------------------------

/**
 * Une note venue du serveur, en nombre, ou `null`. MySQL rend un DECIMAL en
 * chaîne (« 12.50 ») : sans cette lecture, « 12.50 » et 12.5 paraîtraient
 * différents, et une chaîne vide passerait pour un zéro.
 */
export function lireNote(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : null;
}

const FORMAT_NOTE = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** « 12,5 », « 0 » - jamais vide pour un zéro (le carnet, lui, affiche un 0 comme une case vide). */
export function ecrireNote(valeur) {
  const n = lireNote(valeur);
  return n === null ? '' : FORMAT_NOTE.format(n);
}

/** Une note, ou « - » si la case est vide. */
export const noteOuTiret = (valeur) => (lireNote(valeur) === null ? '-' : ecrireNote(valeur));

// ----------------------------------------------------------------------------
// Le report dans le carnet, tel que le serveur l'annonce
// ----------------------------------------------------------------------------

// Les motifs de conflit, en deux ou trois mots pour une colonne ; la phrase
// complète (`prevision.explication`, écrite par le serveur) passe dessous.
export const MOTIFS_COURTS = {
  verrouillee: 'Verrouillée au carnet',
  transferee: "Venue d'un autre site",
  carnet_modifie: 'Modifiée au carnet',
  valeur_conservee: 'Le carnet garde sa note',
  hors_classe: 'Plus dans la classe',
  ligne_mensuelle: 'Note mensuelle au carnet',
  carnet_illisible: 'Ligne du carnet illisible',
  carnet_hors_bareme: 'Carnet hors barème',
  ligne_videe: 'Ligne du carnet avec appréciation',
  doublon_mois: 'Deux lignes au carnet ce mois'
};

/** Un motif de conflit en mots : le libellé court s'il est connu, sinon le texte du serveur. */
export const libelleMotif = (motif) => MOTIFS_COURTS[motif] || (typeof motif === 'string' && motif ? motif : 'Non reportable');

/**
 * Ce que le report fait (ou ferait) de la ligne d'un élève, en mots :
 * `{ libelle, detail, ton }`. `ton` : 'ajout', 'remplacement', 'neutre',
 * 'fait', 'conflit'.
 *
 * La prévision est celle du serveur (`eleve.prevision`) : la page annonce ce
 * que la validation, ou un nouveau report, écrira - ni plus, ni moins.
 */
export function lecturePrevision(eleve, { brouillon = false } = {}) {
  const prevision = eleve?.prevision;
  if (!prevision) {
    if (brouillon) return { libelle: 'Non transmise', detail: null, ton: 'neutre' };
    return { libelle: 'Pas de saisie', detail: null, ton: 'neutre' };
  }
  const carnet = lireNote(eleve?.carnet?.valeur);
  switch (prevision.resultat) {
    case 'insere':
      return { libelle: 'À ajouter au carnet', detail: null, ton: 'ajout' };
    case 'mis_a_jour':
      if (carnet === null) return { libelle: 'À ajouter au carnet', detail: null, ton: 'ajout' };
      if (lireNote(eleve?.valeur) === null) {
        // Seul ce circuit efface, et seulement ce qu'il avait lui-même reporté.
        // La raison se lit sur la ligne : absent, ou case vidée par la
        // rectification - ne jamais écrire « absent » d'un élève qui ne l'est pas.
        return {
          libelle: `Effacera ${ecrireNote(carnet)}`,
          detail: estVrai(eleve?.absent)
            ? "Note reportée par cette feuille ; l'élève est désormais noté absent."
            : 'Note reportée par cette feuille ; sa case est désormais vide.',
          ton: 'remplacement'
        };
      }
      return { libelle: `Remplacera ${ecrireNote(carnet)}`, detail: null, ton: 'remplacement' };
    case 'identique':
      // Le carnet porte déjà la note de la saisie : rien à écrire, et jamais
      // un conflit pour le serveur - même sur une ligne verrouillée ou venue
      // d'un autre site, qui n'est pas réécrite pour autant.
      if (estVrai(eleve?.carnet?.verrouille) || estVrai(eleve?.carnet?.transferee)) {
        return {
          libelle: 'Déjà au carnet',
          detail: estVrai(eleve?.carnet?.verrouille)
            ? 'Ligne verrouillée qui porte déjà la note de la saisie : rien à reporter.'
            : "Ligne venue d'un autre site qui porte déjà la note de la saisie : rien à reporter.",
          ton: 'neutre'
        };
      }
      return { libelle: 'Identique au carnet', detail: null, ton: 'neutre' };
    case 'deja_reporte':
      return { libelle: 'Reportée', detail: null, ton: 'fait' };
    case 'rien_a_ecrire':
      return { libelle: 'Rien à reporter', detail: null, ton: 'neutre' };
    case 'conflit':
      return {
        libelle: libelleMotif(prevision.motif),
        detail: prevision.explication || null,
        ton: 'conflit'
      };
    default:
      return { libelle: prevision.resultat || '-', detail: null, ton: 'neutre' };
  }
}

export const TONS_PREVISION = {
  ajout: 'text-emerald-700',
  remplacement: 'font-semibold text-amber-800',
  neutre: 'text-gray-500',
  fait: 'text-emerald-700',
  conflit: 'font-semibold text-red-700'
};

/**
 * Un conflit à régler : le report n'écrira rien et le carnet diffère de la
 * saisie. Une ligne (verrouillée ou non) qui porte déjà la note de la saisie
 * n'en est pas un : le serveur la dit `identique`.
 */
export const estConflitARegler = (eleve) => eleve?.prevision?.resultat === 'conflit';

/**
 * Le report EFFACERA-t-il une note du carnet ? Seul cas : une note que ce
 * circuit avait reportée, et que la saisie ne porte plus (absent, case vide).
 * Le serveur le range dans `mis_a_jour` ; l'écran le compte à part - effacer
 * une note n'est pas la remplacer.
 */
export const estEffacement = (eleve) => eleve?.prevision?.resultat === 'mis_a_jour'
  && lireNote(eleve?.carnet?.valeur) !== null && lireNote(eleve?.valeur) === null;

// ----------------------------------------------------------------------------
// Journal
// ----------------------------------------------------------------------------

// Les actions que le journal de la feuille enregistre (journal_notes_enseignants).
// Une action inconnue garde un libellé tiré de son nom plutôt qu'un libellé deviné.
const ACTIONS_JOURNAL = {
  ouverture: 'Feuille commencée',
  soumission: 'Soumise à la direction',
  retrait: "Retirée par l'enseignant",
  renvoi: "Renvoyée à l'enseignant",
  validation: 'Validée et reportée au carnet',
  report: 'Report refait',
  rectification_demandee: 'Rectification demandée',
  rectification_autorisee: 'Rectification autorisée',
  rectification_refusee: 'Rectification refusée'
};

export function libelleAction(action) {
  if (ACTIONS_JOURNAL[action]) return ACTIONS_JOURNAL[action];
  const texte = String(action || '').replace(/_/g, ' ').trim();
  return texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : 'Événement';
}

/** « Mme X (direction) », « Arnaud MOUKOKO (enseignant) ». */
export function acteurJournal(entree) {
  const nom = nomEmploye(entree?.acteur);
  const role = entree?.acteur_type === 'enseignant' ? 'enseignant' : (entree?.acteur_type === 'employe' ? 'direction' : '');
  if (!nom) return role ? (role === 'enseignant' ? "L'enseignant" : 'La direction') : '';
  return role ? `${nom} (${role})` : nom;
}

/** Les détails JSON d'une entrée, décodés (le pilote les rend parfois en chaîne). */
export function detailsJournal(details) {
  if (typeof details !== 'string') return details && typeof details === 'object' ? details : null;
  try { return JSON.parse(details); } catch { return null; }
}

/** « 1,2 Mo », « 340 Ko ». */
export function tailleFichier(octets) {
  const n = Number(octets);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} Ko`;
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(n / (1024 * 1024))} Mo`;
}

// ----------------------------------------------------------------------------
// Navigation
// ----------------------------------------------------------------------------

/**
 * Le retour vers la page d'où l'on vient : un bouton bordé, jamais un lien nu
 * (consigne de Daniel), au survol à la couleur de l'administration comme le
 * « Retour » des pages de paramétrage (EnTeteParametrage). `onClick` permet de
 * reculer dans l'historique (filtres retrouvés) quand on vient de la page visée.
 */
export const BoutonRetour = ({ to, onClick = undefined, state = undefined, children }) => (
  <Link
    to={to}
    state={state}
    onClick={onClick}
    className="inline-flex h-11 max-w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 shadow-sm transition-colors hover:border-[#246990] hover:bg-[#246990] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/30"
  >
    <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
    <span className="truncate">{children}</span>
  </Link>
);

// Les filtres d'une liste (statuts, élèves affichés) : pastilles à l'arrondi
// d'un bouton.
export const classesFiltre = (actif) => `h-8 rounded-lg border px-3 text-xs font-semibold transition ${actif
  ? 'border-[#246990] bg-[#246990] text-white'
  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`;

// ----------------------------------------------------------------------------
// Pagination d'une liste de feuilles (50 par page par défaut, 200 au plus : `par_page` du serveur)
// ----------------------------------------------------------------------------

export const PaginationFeuilles = ({ page, pages, total, parPage = 50, onChangerPage, desactive }) => {
  const premier = (page - 1) * parPage + 1;
  const dernier = Math.min(page * parPage, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 py-3">
      <p className="text-[11px] text-gray-500">
        {pages > 1 ? `${premier}-${dernier} sur ${pluriel(total, 'feuille')}` : pluriel(total, 'feuille')}
      </p>
      {pages > 1 && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1" disabled={desactive || page <= 1} onClick={() => onChangerPage(page - 1)}>
            <ChevronLeft className="h-3.5 w-3.5" />
            Précédent
          </Button>
          <span className="text-[11px] font-semibold tabular-nums text-gray-600">{page} / {pages}</span>
          <Button variant="outline" size="sm" className="gap-1" disabled={desactive || page >= pages} onClick={() => onChangerPage(page + 1)}>
            Suivant
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};
